import { TrackingActions, TrackingCategory } from '@buy-viasat/redux/src/analytics';
import {
  AddressType,
  normalizeScrubbedAddressLines,
  resolveFiberMduAddress,
  withNormalizedLine1,
} from '@buy-viasat/redux/src/address';
import { appActions, CustomerType, Modals, selectOrderId, selectUrlParams } from '@buy-viasat/redux/src/app';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import {
  selectAddressLine2,
  selectCoordinates,
  selectHouseNumber,
  selectScrubLocation,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
  serviceabilityActions,
} from '@buy-viasat/redux/src/serviceability';
import { Country, LocationInput, RecommendationStatus, ScrubAddressPayload } from '@buy-viasat/types/build/bv';
import { geocodeByAddress } from 'react-google-places-autocomplete';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { benchmark, resetSession } from 'shared/containers/App/saga';
import scrubAddressAsync, { ScrubAddressResponse } from '../../providers/requests/fulfillment/scrubAddress';
import getPartnerAccountAsync from '../../providers/requests/products/getPartnerAccount';
import clientLogger from '../../utils/clientLogger';
import { ValidateAddressAction } from './types';
import { isDefined, isDefinedString } from '@buy-viasat/utils';
import { planActions, sanitizeAddressLines } from '@buy-viasat/redux/src/plan';
import { PayloadAction } from '@reduxjs/toolkit';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';

export function* addressEquality() {
  yield put(serviceabilityActions.setIsServiceabilityLoading(false));
  yield put(
    appActions.analyticsEvent({
      category: TrackingCategory.SERVICEABILITY_CHECK,
      action: TrackingActions.SUCCESSFUL,
      params: undefined,
    }),
  );
  yield put(serviceabilityActions.useSuggestedServiceAddress());
}

export function* addressInequality() {
  yield put(serviceabilityActions.setIsServiceabilityLoading(false));
  yield put(
    appActions.analyticsEvent({
      category: TrackingCategory.SERVICEABILITY_CHECK,
      action: TrackingActions.MAYBE,
      params: undefined,
    }),
  );
  yield put(appActions.setModalVisible(Modals.CONFIRM_SERVICE_ADDRESS));
}

function* checkAddressValidityOnNewApi(action: PayloadAction<{ address: ScrubAddressPayload }>) {
  yield call(checkValidAddress, { address: action.payload.address });
}

export function* checkValidAddress({ address }: ValidateAddressAction) {
  yield put(
    serviceabilityActions.setScrubLocation({
      address,
      coordinates: { latitude: address.latitude, longitude: address.longitude },
    }),
  );

  if (isDefined(address.latitude) && isDefined(address.longitude) && address.invalidAddress) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.ADDRESS_NOT_VERIFIED));
  } else if (address.invalidAddress) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.NO_ADDRESS_FOUND));
  } else if (address.recommendation === RecommendationStatus.NEEDS_VERIFICATION) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.CONFIRM_SERVICE_ADDRESS));
  } else if (address.recommendation === RecommendationStatus.RECOMMEND) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(serviceabilityActions.useSuggestedServiceAddress());
  } else {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.CONFIRM_SERVICE_ADDRESS));
  }
}

export function* checkBannedPostalCode({ address }: ValidateAddressAction) {
  if (address && address.bannedPostalCode) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SERVICEABILITY_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(appActions.setModalVisible(Modals.NO_SERVICE));
  } else {
    yield call<any>(checkValidAddress, { address });
  }
}

export function* scrubServiceAddressSaga() {
  const address: AddressType = yield select(selectServiceAddressValues);
  const addressLine2: string = yield select(selectAddressLine2);

  try {
    const { data }: ScrubAddressResponse = yield call(scrubAddressAsync, { address });
    const { scrubAddress } = data || {};

    if (!scrubAddress) {
      throw new Error('Scrub address failed');
    }
    yield put(serviceabilityActions.setHasFiberMduAddress(scrubAddress.hasFiberMduAddress));

    // Normalize the per-provider unit shape for the UI only; the raw response is kept below.
    const normalizedScrubAddress: AddressType = {
      ...scrubAddress,
      addressLines: normalizeScrubbedAddressLines(scrubAddress.addressLines),
    };

    const { needsUnit, shouldNormalizeLine1 } = resolveFiberMduAddress(
      scrubAddress.hasFiberMduAddress,
      normalizedScrubAddress.addressLines,
      addressLine2,
    );

    if (needsUnit) {
      yield put(serviceabilityActions.setIsServiceabilityLoading(false));
      return;
    }

    // Keep the exact, unedited addressLines PAL returned so downstream providers
    // (getAvailableProducts / getAddOns) send the same value the backend gave us.
    yield put(serviceabilityActions.setUnsanitizedAddressLines(scrubAddress.addressLines));

    const result: { address: AddressType } = {
      address: normalizedScrubAddress,
    };
    if (shouldNormalizeLine1) {
      result.address = withNormalizedLine1(normalizedScrubAddress, addressLine2);
    }

    yield call<any>(checkBannedPostalCode, result);
  } catch (err: any) {
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    clientLogger('scrubServiceAddressSaga: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SERVICEABILITY_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(appActions.setErrorRetryAction({ type: serviceabilityActions.scrubServiceAddress.type, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

export function* abortIfEmptyAddressLines<T>(addressLines: T[] | undefined | null, sagaName: string): Generator {
  if (!Array.isArray(addressLines) || addressLines.length === 0) {
    console.warn(`Aborting: addressLines is empty in ${sagaName}`);
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.NO_AVAILABLE_PLANS));

    return true;
  }

  return false;
}

export function* onUseSuggestedServiceAddress(): Generator {
  const scrubAddress: any = yield select(selectScrubLocation);
  const { dealerId, partnerId }: any = yield select(selectUrlParams);
  yield put(appActions.setIsModalVisible(false));
  yield put(serviceabilityActions.setServiceAddress(scrubAddress.address));
  yield put(serviceabilityActions.setCoordinates(scrubAddress.coordinates));

  if (yield call(abortIfEmptyAddressLines, scrubAddress.address.addressLines, 'onUseSuggestedServiceAddress')) {
    return;
  }

  if (USE_NEW_API) {
    yield put(navActions.next());
    return;
  }

  try {
    const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
    const { addressLines, countryCode, municipality, region, postalCode } = scrubAddress.address;
    const location: LocationInput = sanitizeAddressLines(
      {
        address: { addressLines, countryCode, municipality, region, postalCode },
        coordinates: scrubAddress.coordinates,
      },
      unsanitizedAddressLines,
    );
    const {
      data: { getPartnerAccount },
    }: any = yield call<any>(getPartnerAccountAsync, {
      location,
      dealerId,
      partnerIraPartyId: partnerId,
    });
    yield put(appActions.setSalesAgreementId(getPartnerAccount.salesAgreementId));
    yield put(appActions.setFulfillmentAgreementId(getPartnerAccount.fulfillmentAgreementId));
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    if (scrubAddress.address.recommendation === RecommendationStatus.RECOMMEND) {
      yield put(navActions.next());
    }
  } catch (err: any) {
    clientLogger('scrubServiceAddressSaga: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SERVICEABILITY_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.NO_AVAILABLE_PLANS));
  }
}

// TODO: add types
export const pullCityAndStateFromGeocodeResults = (results: any): any => {
  const [state] = results.address_components.filter((item: any) => item.types[0] === 'administrative_area_level_1');
  let [city] = results.address_components.filter((item: any) => item.types[0] === 'locality');
  if (!isDefined(city)) {
    [city] = results.address_components.filter((item: any) => item.types[0] === 'administrative_area_level_2');
  }
  if (!isDefined(city)) {
    [city] = results.address_components.filter((item: any) => item.types[0] === 'administrative_area_level_3');
  }
  return {
    stateName: state?.short_name,
    cityName: city?.short_name,
  };
};

export function* onAutoCompletedAddressUsedSaga(): Generator {
  const address: any = yield select(selectServiceAddressValues);
  const { dealerId, partnerId }: any = yield select(selectUrlParams);
  const coordinates: any = yield select(selectCoordinates);

  if (yield call(abortIfEmptyAddressLines, address.addressLines, 'onAutoCompletedAddressUsedSaga')) {
    return;
  }

  try {
    const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
    const location: LocationInput = sanitizeAddressLines({ address, coordinates }, unsanitizedAddressLines);
    const {
      data: { getPartnerAccount },
    }: any = yield call<any>(getPartnerAccountAsync, {
      location,
      dealerId,
      partnerIraPartyId: partnerId,
    });
    yield put(appActions.setSalesAgreementId(getPartnerAccount.salesAgreementId));
    yield put(appActions.setFulfillmentAgreementId(getPartnerAccount.fulfillmentAgreementId));
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(navActions.next());
  } catch (err: any) {
    clientLogger('scrubServiceAddressSaga: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SERVICEABILITY_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.NO_AVAILABLE_PLANS));
  }
}

export function* onUseInlineServiceAddress(): Generator {
  const inlineServiceAddress: any = yield select(selectServiceAddressValues);

  if (yield call(abortIfEmptyAddressLines, inlineServiceAddress.addressLines, 'onUseInlineServiceAddress')) {
    return;
  }

  const { houseNumber }: any = yield select(selectHouseNumber);
  const { dealerId, partnerId }: any = yield select(selectUrlParams);

  yield put(appActions.setIsModalVisible(false));
  try {
    const postalCode = inlineServiceAddress.postalCode.split('-');
    const geoCodeResults: any = yield call<any>(geocodeByAddress, postalCode[0]);
    const { cityName, stateName } = pullCityAndStateFromGeocodeResults(geoCodeResults[0]);
    const coordResults: any = yield call<any>(
      geocodeByAddress,
      `${inlineServiceAddress.addressLines[0]},${cityName},${stateName}+${postalCode}`,
    );
    const { lat, lng } = coordResults[0].geometry.location;

    const coordinates = { latitude: lat(), longitude: lng() };
    const formattedServiceAddress = { ...inlineServiceAddress };
    if (isDefinedString(houseNumber)) {
      formattedServiceAddress['houseNumber'] = houseNumber;
    }
    yield put(serviceabilityActions.setServiceAddress(formattedServiceAddress));
    yield put(serviceabilityActions.setServiceAddressMunicipality(cityName));
    yield put(serviceabilityActions.setServiceAddressRegion(stateName));
    yield put(serviceabilityActions.setCoordinates(coordinates));

    const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
    const location: LocationInput = sanitizeAddressLines(
      {
        address: { ...inlineServiceAddress, municipality: cityName, region: stateName },
        coordinates,
      },
      unsanitizedAddressLines,
    );
    const {
      data: { getPartnerAccount },
    }: any = yield call<any>(getPartnerAccountAsync, {
      location,
      dealerId,
      partnerIraPartyId: partnerId,
    });
    yield put(appActions.setSalesAgreementId(getPartnerAccount.salesAgreementId));
    yield put(appActions.setFulfillmentAgreementId(getPartnerAccount.fulfillmentAgreementId));
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(navActions.next());
  } catch (err: any) {
    clientLogger('scrubServiceAddressSaga: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SERVICEABILITY_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(serviceabilityActions.setIsServiceabilityLoading(false));
    yield put(appActions.setModalVisible(Modals.NO_AVAILABLE_PLANS));
  }
}

function* onServiceabilityMount() {
  yield put(serviceabilityActions.resetServiceAddress());
  yield put(appActions.setCurrentRoute(Routes.SERVICEABILITY));
  const orderId: string = yield select(selectOrderId);
  if (!!orderId) yield call(resetSession);
  yield put(planActions.setHavePlansLoaded(false));
  yield put(
    planActions.setPlanId({
      id: '',
      name: '',
      offerId: '',
      extensionProducts: [],
      planPrice: 0,
      planPromo: 0,
      inflectionPointText: '',
      productFamily: '',
      status: '',
      isRegulated: false,
      isCafII: false,
      dataCap: '',
      customerType: CustomerType.R,
      country: Country.US,
    }),
  );
}

export function* serviceabilitySaga(): Generator {
  yield takeLatest(serviceabilityActions.onServiceabilityMount.type, onServiceabilityMount);
  yield takeLatest(serviceabilityActions.scrubServiceAddress.type, benchmark(scrubServiceAddressSaga));
  yield takeLatest(serviceabilityActions.useSuggestedServiceAddress.type, onUseSuggestedServiceAddress);
  yield takeLatest(serviceabilityActions.useInlineServiceAddress.type, onUseInlineServiceAddress);
  yield takeLatest(serviceabilityActions.onAutoCompletedAddressUsed.type, onAutoCompletedAddressUsedSaga);
  yield takeLatest(serviceabilityActions.validateNewApiScrubbedAddress.type, checkAddressValidityOnNewApi);
}
